import Gleap from 'gleap'
import { appWithTranslation, SSRConfig } from 'next-i18next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { Manrope as HeadingFont } from 'next/font/google'
import { useEffect, useState } from 'react'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import AnalyticsScripts from '~/components/AnalyticsScripts'
import CookiesBanner, { CookieConsent } from '~/components/CookiesBanner'
import GoogleTagManagementPublic from '~/components/GoogleTagManagementPublic'
import configuration from '~/configuration'
import { IUserInformation, Maybe } from '~/core/@types/global'
import '../src/styles/globals.css'

const AppRouteLoadingIndicator = dynamic(
  () => import('~/core/ui/AppRouteLoadingIndicator'),
  {
    ssr: false
  }
)

const fontFamilyHeading = HeadingFont({
  subsets: ['latin'],
  variable: '--font-family-heading',
  fallback: ['--font-family-sans'],
  preload: true,
  weight: ['300', '400', '500', '600']
})

interface DefaultPageProps extends SSRConfig {
  user?: Maybe<IUserInformation>
}

function App(
  props: AppProps<DefaultPageProps> & { pageProps: DefaultPageProps }
) {
  const { Component } = props
  const pageProps = props.pageProps as DefaultPageProps
  const [cookieConsent, setCookieConsent] = useState<CookieConsent>(null)

  // Check for existing consent on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedConsent = localStorage.getItem(
        'cookie-consent'
      ) as CookieConsent
      setCookieConsent(storedConsent)
    }
  }, [])

  if (
    typeof window !== 'undefined' &&
    configuration.key.gleapApiKey &&
    Gleap &&
    Gleap.getInstance().initialized === false
  ) {
    Gleap.initialize(configuration.key.gleapApiKey)
  }

  const handleConsentChange = (consent: CookieConsent) => {
    setCookieConsent(consent)
  }

  const shouldLoadAnalytics = cookieConsent === 'accepted'

  return (
    <>
      {shouldLoadAnalytics && <GoogleTagManagementPublic />}
      <AnalyticsScripts enabled={shouldLoadAnalytics} />
      <AppRouteLoadingIndicator />
      <FontFamily />
      <Component {...pageProps} />
      <CookiesBanner onConsentChange={handleConsentChange} />
    </>
  )
}

export default appWithTranslation(App)

function FontFamily() {
  return (
    <style jsx global>
      {`
        html {
          --font-family-sans: ${fontFamilyHeading.style.fontFamily}, 'Ubuntu',
            'sans-serif';
          --font-family-heading: ${fontFamilyHeading.style.fontFamily}, 'Ubuntu',
            'sans-serif';
        }
      `}
    </style>
  )
}
